<script setup>
import { onMounted, ref, watchEffect, watch } from 'vue';
import { useCommonStore } from '@/stores/common';
import axios from 'axios';

const store = useCommonStore();
const iconsURL = ref(store.cloudfrontIconURL);
const InsigntEndpoint = ref(store.insigntsURL());
const props = defineProps(['insightType','relatedInsights'])
const type = ref(props.insightType);
const relatedInsights = ref(props.relatedInsights);
const isHomepage = ref(false);
const isArticle = ref(false);
const isLoading = ref(true);
const isError = ref(false);
const articles = ref([]);
const isDesktop = ref(true);
const allInsights = ref([]);
const pwcInsights = ref(['2af79c67-408f-45fe-8e7c-d56e28cb5ec3','edac0a02-1950-4008-8e13-e2db87e9ecdb','27d81851-6973-4c3a-a93e-7ee8a93c6827','1a60650c-0eea-41a0-afa7-7311863c8f2d']);
const responsiveOptions = ref([
    {
        breakpoint: '700px',
        numVisible: 1,
        numScroll: 1
    }
]);

watchEffect(() => {
    if(props.relatedInsights === undefined) {
        relatedInsights.value = [];
        getArticles();
    } else {
        relatedInsights.value = props.relatedInsights;
    }
});

watch(relatedInsights, () => {
    getArticles();
});

async function getArticles() {
    await axios.get(InsigntEndpoint.value)
        .then(res => {
            if(props.insightType === 'pwc') {
                allInsights.value = res.data;
                getPwCInsights();
            } else {
                const results = res.data;
                if(relatedInsights.value.length === 0) {
                    articles.value = results.slice(0,3);
                } else if(relatedInsights.value.length === 3) {
                    results.forEach(elm => {
                        if(relatedInsights.value.includes(elm.uuid)) {
                            articles.value.push(elm);
                        }
                    });
                } else {
                    var needed = 3 - relatedInsights.value.length;
                    results.forEach(elm => {
                        if(relatedInsights.value.includes(elm.uuid)) {
                            articles.value.push(elm);
                        }
                    });
                    while(needed !== 0) {
                        articles.value.push(results[needed]);
                        needed--;
                    }
                }     
            }
            isLoading.value = false;
        })
        .catch(err => {
            isLoading.value = false;
            isError.value = true;
            console.log(err);
        });
}

function getPwCInsights() {
    var shuffled = [...pwcInsights.value];
    for(let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    var picked = shuffled.slice(0,3);

    allInsights.value.forEach(elm => {
        if(picked.includes(elm.uuid)) {
            articles.value.push(elm);
        }
    });
}

function formatArticleTime(time,type) {
    return 'Reading Time: ' + time + ' minutes.';
}

function formatArticleLink(uuid) {
    const link = '/insightsarticle?id=' + uuid;
    return link;
}

function viewportChange() {
    const viewportWidth = window.innerWidth;
    if(viewportWidth >= 1920) {
        isDesktop.value = true;  
    } else if(viewportWidth < 1920 && viewportWidth >= 1366) {
        isDesktop.value = true;
    } else if(viewportWidth < 1366 && viewportWidth >= 768) {
        isDesktop.value = false;
    } else if(viewportWidth < 768 ) {
        isDesktop.value = false;
    }
}

onMounted(() => {
    viewportChange();
    window.addEventListener("resize", viewportChange)
    //getArticles();
    
    if(props.insightType === 'homepage') {
        isHomepage.value = true;
        isArticle.value = false;
    }
    if(props.insightType === 'retcalc') {
        isHomepage.value = true;
        isArticle.value = false;
    }
    if(props.insightType === 'article') {
        isHomepage.value = false;
        isArticle.value = true;
    }
    if(props.insightType === 'pwc') {
        isHomepage.value = true;
        isArticle.value = false;
    }
});
</script>

<template>
    <div id="clarity-insights" :class="{'clarity-insights-homepage': isHomepage, 'clarity-insights-article': isArticle}">
        <div v-if="type === 'homepage' || type ==='pwc'">
            <h2 class="clarity-h2-blue text-center clarity-margin-top-2el mb-[20px]!">INSIGHTS</h2>
            <p class="clarity-body-text text-center mb-[20px]!">Explore expert guides on ISAs, Capital Gains Tax, and more, plus essential financial planning insights to help manage investments and preserve wealth.</p>
        </div>
        <div v-if="type === 'article'">
            <h2 class="clarity-h2-blue text-center mb-[20px]!">LATEST INSIGHTS</h2>
        </div>
        <div v-if="type === 'retcalc'">
            <h2 class="clarity-h2-blue text-center clarity-margin-top-2el mb-[20px]!">INSIGHTS</h2>
            <p class="clarity-body-text text-center mb-[20px]!">Read our latest articles on retirement planning, including tax tips, lifestyle considerations, and market updates.</p>
        </div>
        <div v-if="isLoading" class="flex justify-center flex-wrap">
            <ProgressSpinner></ProgressSpinner>
        </div>
        <div v-else-if="isError" class="flex justify-center flex-wrap">
            <div id="articles-error-box">
                <p class="articles-error-box-title"><img :src="iconsURL+'error-icon.png'" alt="Error Icon"> INSIGHTS</p>
                <p class="articles-error-box-message">Insights are currently unavailable</p>
            </div>    
        </div>
        <div v-else id="articles">
            <div v-if="isDesktop" class="grid grid-cols-3 gap-4">   
                <div v-for="article in articles" class="article">
                    <a :href="formatArticleLink(article.uuid)"><img :src="article.image" alt="Article Image"></a>
                    <div class="article-title">
                        <a :href="formatArticleLink(article.uuid)"><h3 class="clarity-h3-light-blue" v-html="article.title"></h3></a>
                    </div>
                    <div class="flex mb-[1rem]">
                        <div class="insight-tag">{{ article.type }}</div>
                        <div class="insight-reading-time">{{ formatArticleTime(article.time,article.type) }}</div>
                    </div>
                    <div class="flex flex-col justify-between h-[10rem]">
                        <p class="clarity-body-text">{{ article.summary }}</p>
                        <a class="more-link" :href="formatArticleLink(article.uuid)">Open Insight</a>   
                    </div> 
                </div>
            </div> 
            <div v-else>
                <Carousel :value="articles" :numVisible="2" :numScroll="1" :showIndicators="false" :responsiveOptions="responsiveOptions">
                    <template #item="slotProps">
                        <div class="article">
                            <a :href="formatArticleLink(slotProps.data.uuid)"><img :src="slotProps.data.image" alt="Article Image"></a>
                            <a :href="formatArticleLink(slotProps.data.uuid)"><h3 class="clarity-h3-light-blue" v-html="slotProps.data.title"></h3></a>
                            <div class="flex">
                                <div class="insight-tag">{{ slotProps.data.type }}</div>
                                <div class="insight-reading-time">{{ formatArticleTime(slotProps.data.time,slotProps.data.type) }}</div>
                            </div>
                            <div class="flex flex-col justify-between min-h-[10rem]">
                                <p class="clarity-body-text">{{ slotProps.data.summary }}</p>
                                <a class="more-link" :href="formatArticleLink(slotProps.data.uuid)">Open Insight</a>
                            </div>
                        </div>
                    </template>
                </Carousel>
            </div>
        </div>
        <div class="flex justify-center clarity-margin-top-m">
            <a href="/insights"><button class="clarity-button-grey view-more-btn">View more</button></a>
        </div>
    </div>
</template>

<style lang="css" scoped>
    #clarity-insights {
        width: 100%;
        padding-left: calc(15%);
        padding-right: calc(15%);
    }
    .clarity-insights-homepage {
        padding-bottom: 150px;
        background-color: var(--clarity-pure-white);
    }
    .clarity-insights-article {
        padding-top: 120px;
        padding-bottom: 150px;
        background-color: var(--clarity-snow-grey);
    }
    .article {
        margin-top: 40px;
    }
    .article img {
        width: 100%;
        border-radius: 27px;
        margin-bottom: 30px;
    }
    .insight-tag {
        margin-right: 10px;
        margin-top: 22px;
        padding: 2px 15px;
        letter-spacing: var(--clarity-character-spacing-0);
        color: var(--clarity-pure-white);
        text-align: center;
        font: normal normal normal 12px/18px Open Sans;
        background: transparent linear-gradient(180deg, #179BD7 0%, #0058A9 100%) 0% 0% no-repeat padding-box;
        border-radius: 11px;
        opacity: 1;
    }
    .insight-reading-time {
        margin-top: 22px;
        letter-spacing: var(--clarity-character-spacing-0);
        color: var(--clarity-grey);
        font: normal normal normal 12px/18px Open Sans;
        opacity: 1;
    }
    .more-link {
        letter-spacing: var(--clarity-character-spacing-0);
        color: var(--clarity-light-blue);
        font: normal normal normal 18px/30px Open Sans;
        opacity: 1;
    }
    .view-more-btn {
        width: 182px;
        height: 54px;
        padding: 15px 45px;
        /* margin-bottom: 120px; */
    }
    #articles-error-box {
        width: 50rem;
        padding: 30px;
        background: transparent linear-gradient(180deg, #F2686E33 0%, #D131384D 100%) 0% 0% no-repeat padding-box;
        box-shadow: 0px 5px 20px #0000000D;
        border: 1px solid #D1313880;
        border-radius: 27px;
        opacity: 1;
    }
    #articles-error-box img {
        vertical-align: top;
    }
    .articles-error-box-title {
        margin: 0px;
        letter-spacing: var(--clarity-character-spacing--0-22);
        color: var(--clarity-red);
        font: normal normal normal 22px/32px Josefin Sans;
        text-transform: uppercase;
        opacity: 1;
    }
    .articles-error-box-message {
        margin: 0px;
        letter-spacing: var(--clarity-character-spacing-0);
        color: var(--clarity-dark-grey);
        text-align: left;
        font: normal normal normal 18px/28px Open Sans;
        opacity: 1;
    }
    .article-title {
        height: 85px;
    }

    /* Default Big Screen 1920px */
    @media only screen and (max-width: 1470px) {}
    /* Mid Screen */
    @media only screen and (max-width: 1365px) {
        #clarity-insights {
            padding-left: calc(9%);
            padding-right: calc(9%);
        }
        .article {
            margin-right: 10px;
            margin-left: 10px;
        }
    }
    /* Small Screen */
    @media only screen and (max-width: 1024px) {
        .article {
            margin-right: 10px;
            margin-left: 10px;
        }
    }
    /* Tablet */
    @media only screen and (max-width: 767px) {
        #clarity-insights {
            padding-left: 20px;
            padding-right: 20px;
        }
    }
    /* Mobile */
    @media only screen and (max-width: 375px) {}
</style>